import React from "react";

import {
  HttpError,
  useApiUrl,
  useDataProvider,
  useNotification,
} from "@refinedev/core";
import { Loader2, Trash } from "lucide-react";

import { UserAvatar } from "@/components/common/user-avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMembers } from "@/contexts/member-context";
import { parseApplicationError } from "@/lib/extensions/httpErrorExtension";

export default function SettingsMembersPage() {
  const { open } = useNotification();

  const apiUrl = useApiUrl();
  const { members, refetch } = useMembers();

  const { custom } = useDataProvider()();
  const [toInvite, setToInvite] = React.useState({});
  const [status, setStatus] = React.useState<"loading" | "success" | "error">();

  return (
    <div className="space-y-6 text-foreground">
      <div>
        <h3 className="text-lg font-medium">Members</h3>
        <p className="text-sm text-muted-foreground">
          Update members settings. See users with access to the dashboard.
        </p>
      </div>

      <Separator className="bg-foreground/10" />
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline">Add Member</Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Invite member</DialogTitle>
            <DialogDescription>
              Add user's mood email (google | email signin). Click save when
              you're done.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                className="col-span-3"
                onChange={(e) =>
                  setToInvite((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="role" className="text-right">
                Role
              </Label>
              <Select
                onValueChange={(value) =>
                  setToInvite((prev) => ({ ...prev, role: value }))
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="role" className="text-right">
                Related Id
              </Label>
              <Input
                id="relatedId"
                className="col-span-3"
                onChange={(e) =>
                  setToInvite((prev) => ({
                    ...prev,
                    relatedIds: [e.target.value],
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={async () => {
                try {
                  setStatus("loading");
                  await custom?.({
                    url: `${apiUrl}/users/invite`,
                    method: "post",
                    payload: toInvite,
                  });
                  refetch?.();
                  setStatus("success");
                } catch (error: HttpError | any) {
                  setStatus("error");
                  open?.({
                    type: "error",
                    message: "Failed to invite member",
                    description:
                      parseApplicationError(error) ?? "Unknown error",
                  });
                }
              }}
              type="submit"
              disabled={status === "loading"}
            >
              {status === "loading" && (
                <Loader2 className="mr-2 w-5 h-5 animate-spin" />
              )}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Card>
        <CardContent>
          <Table className="table-auto">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead colSpan={1 / 3}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>
                    <UserAvatar {...member} full />
                  </TableCell>
                  <TableCell>
                    <Badge>{member.adminMetadata?.role}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant={"ghost"}
                      onClick={async () => {
                        try {
                          setStatus("loading");
                          await custom?.({
                            url: `${apiUrl}/users/remove`,
                            method: "post",
                            payload: {
                              email: member.email,
                            },
                          });
                          refetch?.();
                          setStatus("success");
                        } catch (error: HttpError | any) {
                          setStatus("error");
                          open?.({
                            type: "error",
                            message: "Failed to invite member",
                            description:
                              parseApplicationError(error) ?? "Unknown error",
                          });
                        }
                      }}
                    >
                      <Trash className="text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
